import TableOnly from "components/DataTable/TableOnly";
import ShippingAddressGroup, { ScanStat } from "./ShippingAddressGroup";
import WorkOrderProductsModal from "./WorkOrderProductsModal";
import AddressValidationModal from "./AddressValidationModal";
import AddressHistoryModal from "components/AddressHistory/AddressHistoryModal";
import CombineAddressesModal from "./CombineAddressesModal";
import CopyToShipmentsModal from "./CopyToShipmentsModal";
import CopyAllToShipments from "./CopyAllToShipments";
import RefreshButton from "components/RefreshButton/RefreshButton";
import PaginationWithState from "components/Pagination/PaginationWithState";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Nova Shipping — the mirror of the shop-floor server's bundle data, pushed up
 * by the BundleSync desktop app. (The app and its API routes keep the
 * bundle-sync name; only what staff see is "Nova Shipping".)
 *
 * Listed one row per INVOICE, with the bundles behind a Details button: live
 * data runs ~89 bundles per invoice, so a per-bundle table showed 1,514 rows
 * for 17 actual shipments.
 *
 * Everything from the source is READ ONLY here; the sync overwrites it whenever
 * anything changes upstream. The single editable thing is the W-ID. It is
 * stored per bundle but owned by the source ORDER — saving writes it to every
 * bundle on that order, so editing from any one row changes them all. The
 * modal says so, because the table makes it look like a per-row edit.
 *
 * The W-ID is also derived automatically on ingest from the source's own
 * erp_order_number, which IS the W-ID upstream; a human edit here wins over
 * the source from then on.
 */

const SEARCH_OPTIONS = [
  { value: "", label: "All fields" },
  { value: "bundle_number", label: "Bundle #" },
  { value: "invoice_number", label: "Invoice #" },
  { value: "awb_number", label: "AWB #" },
  { value: "package_number", label: "Package #" },
  { value: "order_id", label: "Order ID" },
  { value: "order_description", label: "Description" },
  { value: "source_company_name", label: "Company" },
];

// The page's default view: one row per invoice. A bundle is not the unit
// staff think in -- live data is ~89 bundles per invoice, so the per-bundle
// table buried 17 real shipments under 1,514 rows. Per-bundle detail moves
// behind the Details button.
//
// company / order / location are counts, not values: they genuinely vary
// within an invoice (one invoice spans up to 29 companies), so showing any
// single one would be a lie.
const invoice_table_head = [
  { name: "Invoice #", value: "invoice_number" },
  { name: "Invoice Date", value: "invoice_date" },
  { name: "AWB #", value: "awb_number" },
  { name: "Bundles", value: "bundleCount", centerAlign: true },
  { name: "Orders", value: "orderCount", centerAlign: true },
  { name: "Companies", value: "companyCount", centerAlign: true },
  { name: "W-ID Linked", value: "mappedOrderCount", centerAlign: true },
  // What the RFID handheld found the last time the WHOLE invoice was checked.
  // The address cards inside Details answer "what goes in this box"; this one
  // answers "did the shipment arrive", which is why it counts every bundle on
  // the invoice rather than only the open, W-ID-linked ones.
  { name: "Scanned", value: "lastScan", centerAlign: true },
  { name: "Actions", value: "Actions" },
];

const fmtDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
    d.getDate(),
  ).padStart(2, "0")}/${String(d.getFullYear()).slice(2)}`;
};

export default function NovaShipping() {
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filterBy, setFilterBy] = useState("");
  const [mappedFilter, setMappedFilter] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Shipped-status tabs. "" is All -- an empty string rather than an "all"
  // literal, so it drops out of the request body on its own.
  const [shippedStatus, setShippedStatus] = useState("pending");

  // Company locations. The page-level location tabs were removed; this is
  // kept for the location dropdown INSIDE the invoice Details modal, which is
  // the only place location still filters anything.
  const [locationsList, setLocationsList] = useState([]);

  // Invoice + AWB dropdown. Paged from the server -- the mirror is far too big
  // to filter a <select> in the browser.
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceOffset, setInvoiceOffset] = useState(0);
  const [invoiceHasMore, setInvoiceHasMore] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  // Per-invoice bundle detail, from /bundle-sync/bundle-groups.
  const [detailInvoice, setDetailInvoice] = useState(null);
  // Bundles grouped shipping address -> order (see /bundle-sync/bundle-groups).
  // `openGroups` is keyed on the group key so expanding one address does not
  // collapse the others.
  const [detailGroups, setDetailGroups] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  // order_id whose shipped flag is mid-save, so its checkbox is disabled and
  // cannot be double-toggled into a race.
  const [shippingOrderId, setShippingOrderId] = useState(null);
  // Group key currently being marked shipped, same reason.
  const [shippingGroupKey, setShippingGroupKey] = useState(null);
  // The groups whose addresses are being validated, or null. A LIST because
  // the same modal serves one group and the whole invoice; held as whole
  // groups because the modal needs their work orders and header identity.
  const [validatingGroups, setValidatingGroups] = useState(null);
  // Set only by the per-group Re-validate button. "Validate all" leaves it
  // false on purpose: skipping addresses that already have a stored answer is
  // the point of that button, and forcing there would put every address back
  // through UPS on every press.
  const [validateForce, setValidateForce] = useState(false);
  // Combine is a different job from Validate: it merges several groups onto
  // ONE address rather than checking each group's own.
  const [combiningGroups, setCombiningGroups] = useState(null);
  // "combine" (write as-is) or "validate" (ask UPS first). Held next to the
  // groups because the two are set together and cleared together.
  const [combineMode, setCombineMode] = useState("validate");
  // Work order whose shipping address history is open, or null.
  const [historyWoId, setHistoryWoId] = useState(null);
  // Shipped addresses drop out of the queue. Kept as a toggle rather than a
  // hard filter: "where did it go" is the first question after ticking one,
  // and there has to be an answer that is not "re-query the database".
  const [showShipped, setShowShipped] = useState(false);
  // Invoice whose "Copy to Shipments" modal is open, or null.
  const [copyInvoice, setCopyInvoice] = useState(null);
  // Work order whose PRODUCTS are open, from a W-ID badge.
  const [productsWoId, setProductsWoId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTotalRows, setDetailTotalRows] = useState(0);

  // Details modal left rail: who and what is on this invoice, plus the
  // current selection. `selectedCustomer` narrows the work-order card and the
  // bundles; `selectedWo` replaces the bundle table with the work order's own
  // details. Selecting a work order does not clear the customer -- the rail
  // stays where the user left it so they can step through several.
  const [parties, setParties] = useState(null);
  const [partiesLoading, setPartiesLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Rail search (name or Cust ID) and the modal's own location filter. The
  // search is client-side because the rail is the whole customer list already
  // -- an invoice tops out around 50 -- so there is nothing to page through.
  //
  // Typed vs applied, like the page's own search above the invoice table:
  // the rail filters on the APPLIED value, so a half-typed name does not
  // empty the list under the user mid-word.
  const [customerSearch, setCustomerSearch] = useState("");
  const [appliedCustomerSearch, setAppliedCustomerSearch] = useState("");
  const [detailLocation, setDetailLocation] = useState("");

  const [editRow, setEditRow] = useState(null);
  const [editWoId, setEditWoId] = useState("");
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Work-order picker inside the modal.
  const [woQuery, setWoQuery] = useState("");
  const [woResults, setWoResults] = useState(null);
  const [woSearching, setWoSearching] = useState(false);
  const [selectedWo, setSelectedWo] = useState(null);

  const getRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.post(
        `/bundle-sync/invoice-summary?page=${pageNumber}&limit=${pageLimit}`,
        {
          query: appliedSearch || undefined,
          filterBy: filterBy || undefined,
          mapped: mappedFilter || undefined,
          invoiceNumber: selectedInvoice?.invoice_number || undefined,
          shippedStatus: shippedStatus || undefined,
          includeDeleted,
        },
        { headers: authHeader() },
      );
      setRows(data?.data || []);
      setTotalPages(data?.totalPages || 0);
      setTotalRows(data?.totalRows || 0);
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
    setLoading(false);
  }, [
    dispatch,
    pageNumber,
    pageLimit,
    appliedSearch,
    filterBy,
    mappedFilter,
    includeDeleted,
    selectedInvoice,
    shippedStatus,
  ]);

  useEffect(() => {
    getRows();
  }, [getRows]);

  const getLocations = useCallback(async () => {
    try {
      const { data } = await API.get("/locations", { headers: authHeader() });
      setLocationsList(Array.isArray(data) ? data : []);
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
  }, [dispatch]);

  useEffect(() => {
    getLocations();
  }, [getLocations]);

  /**
   * Invoice options, ten at a time.
   *
   * `append` is what separates "Load more" from a fresh search: searching
   * replaces the list, loading more adds to it. Both go to the server, so the
   * search covers the whole table rather than the ten rows already loaded.
   */
  const loadInvoiceOptions = async ({ append = false, search } = {}) => {
    const offset = append ? invoiceOffset : 0;
    const term = search !== undefined ? search : invoiceSearch;
    setInvoiceLoading(true);
    try {
      const { data } = await API.post(
        `/bundle-sync/invoices?limit=10&offset=${offset}`,
        { query: term || undefined },
        { headers: authHeader() },
      );
      const rows = data?.data || [];
      setInvoiceOptions((prev) => (append ? [...prev, ...rows] : rows));
      setInvoiceHasMore(!!data?.hasMore);
      setInvoiceOffset(data?.nextOffset ?? 0);
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
    setInvoiceLoading(false);
  };

  const openInvoiceDropdown = () => {
    setInvoiceOpen((open) => {
      if (!open && invoiceOptions.length === 0) loadInvoiceOptions();
      return !open;
    });
  };

  const pickInvoice = (option) => {
    setPageNumber(1);
    setSelectedInvoice(option);
    setInvoiceOpen(false);
  };

  const changeShippedStatus = (value) => {
    // Back to page 1: the tabs select different invoices, so staying on page 4
    // would land on a page that may not exist in the new selection.
    setPageNumber(1);
    setShippedStatus(value);
  };

  const changeLimit = (value) => {
    setPageNumber(1);
    setPageLimit(value);
  };

  const applySearch = (e) => {
    e.preventDefault();
    // Always back to page 1: a search run from page 40 would otherwise ask for
    // page 40 of the results and look like "no matches".
    setPageNumber(1);
    setAppliedSearch(searchTerm.trim());
  };

  const clearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setFilterBy("");
    setMappedFilter("");
    setPageNumber(1);
  };

  // Marking an order shipped covers every bundle on it, server-side.
  //
  // Updated optimistically then reconciled: the checkbox has to feel instant,
  // but a failed request must not leave a tick implying a shipment that was
  // never recorded -- so the row is rolled back and the error surfaced.
  const toggleOrderShipped = async (order, shipped) => {
    const orderId = order.order_id;
    const applyLocally = (value) =>
      setDetailGroups((prev) =>
        prev.map((g) => ({
          ...g,
          orders: g.orders.map((o) =>
            o.order_id === orderId
              ? { ...o, shipped: value, shippedAt: value ? new Date().toISOString() : null }
              : o,
          ),
        })),
      );

    setShippingOrderId(orderId);
    applyLocally(shipped);
    try {
      const { data } = await API.post(
        "/bundle-sync/order-shipped",
        { order_id: orderId, shipped },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Could not save");
      // A save that matched no bundles is not a success worth showing as one.
      if (data.bundlesUpdated === 0) {
        applyLocally(!shipped);
        dispatch(
          setNotification({
            message: data.message || "Nothing was updated",
            type: "error",
          }),
        );
      } else {
        // The rail counts shipped bundles per customer, so it has to be
        // re-read or a customer who just finished keeps sitting in the list.
        getParties();
      }
    } catch (err) {
      applyLocally(!shipped);
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
    setShippingOrderId(null);
  };

  // Accepts either shape: a flat bundle row from /list (`mappedWoId`) or a
  // grouped order row from /bundle-groups (`woId`). Both name the same source
  // order, which is what the mapping is actually keyed on.
  const openEdit = (row) => {
    const woId = row.mappedWoId ?? row.woId ?? null;
    setEditRow(row);
    setEditWoId(woId ?? "");
    setEditNote(row.mappingNote ?? "");
    setWoQuery("");
    setWoResults(null);
    setSelectedWo(
      woId
        ? {
            woId,
            jobName: row.linkedJobName ?? row.jobName,
            customerName: row.linkedCustomerName ?? row.source_company_name,
            locationName: row.erpLocationName,
          }
        : null,
    );
  };

  const searchWorkOrders = async (e) => {
    e.preventDefault();
    if (!woQuery.trim()) return;
    setWoSearching(true);
    try {
      const { data } = await API.post(
        "/bundle-sync/work-orders",
        { query: woQuery.trim() },
        { headers: authHeader() },
      );
      setWoResults(data?.data || []);
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
    setWoSearching(false);
  };

  const pickWorkOrder = (wo) => {
    setSelectedWo(wo);
    setEditWoId(wo.woId);
    setWoResults(null);
  };

  const saveMapping = async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      const { data } = await API.post(
        "/bundle-sync/order-map",
        {
          order_id: editRow.order_id,
          woId: editWoId === "" ? "" : Number(editWoId),
          note: editNote,
        },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Could not save");
      dispatch(
        setNotification({
          message: `Order ${editRow.order_id} updated`,
          type: "success",
        }),
      );
      setEditRow(null);
      // Refresh both views: the invoice row's "W-ID Linked" count and, when
      // the edit came from inside the Details modal, the bundle rows behind
      // it. Skipping the second leaves the modal showing the old W-ID.
      await Promise.all([getRows(), detailInvoice ? getDetailRows() : null]);
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
    setSaving(false);
  };

  /**
   * Mark every order at one address shipped (or un-ship it).
   *
   * ONE request for the whole group: ticking nine orders individually can
   * half-succeed and leave the address in a state nobody chose. Optimistic,
   * with a rollback on both a failed request AND on success-but-nothing-updated
   * -- a tick left standing would claim a shipment that was never recorded.
   */
  /**
   * Clear one address group's RFID scan result.
   *
   * The scan describes a pile of boxes at a moment in time. Once the pile is
   * re-packed the old answer is not history -- it is a wrong answer sitting on
   * the screen, so Reset deletes it and the column goes back to "not scanned".
   * Bundles are untouched.
   */
  const resetGroupScan = async (group) => {
    try {
      const { data } = await API.post(
        "/bundle-sync/scan-reset",
        { addressKey: group.key },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Reset failed");
      dispatch(
        setNotification({
          message: `Scan result cleared for ${
            group.address?.addressCompany || group.customerName || "this address"
          }`,
          type: "success",
        }),
      );
      getDetailRows();
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
  };

  /**
   * Clear one INVOICE's RFID scan result.
   *
   * Same reasoning as resetGroupScan above, one level up. `scope` is the only
   * thing that tells the endpoint which of the two kinds of scan to clear; an
   * address reset keeps sending addressKey and no scope, and the server reads
   * that as 'address', so this is additive rather than a change of protocol.
   *
   * A refused reset is reported, not swallowed: until the endpoint understands
   * the invoice scope it answers success:false with a reason, and a Reset that
   * appears to do nothing is the worst of the possible outcomes.
   */
  const resetInvoiceScan = async (invoice) => {
    try {
      const { data } = await API.post(
        "/bundle-sync/scan-reset",
        { scope: "invoice", invoiceNumber: invoice.invoice_number },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Reset failed");
      dispatch(
        setNotification({
          message: `Scan result cleared for invoice ${invoice.invoice_number}`,
          type: "success",
        }),
      );
      getRows();
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
  };

  const shipGroup = async (group, shipped) => {
    const orderIds = group.orders
      .map((o) => o.order_id)
      .filter((n) => n !== null && n !== undefined);
    if (!orderIds.length) return;

    const before = detailGroups;
    setShippingGroupKey(group.key);
    setDetailGroups((prev) =>
      prev.map((g) =>
        g.key === group.key
          ? { ...g, orders: g.orders.map((o) => ({ ...o, shipped })) }
          : g,
      ),
    );

    try {
      const { data } = await API.post(
        "/bundle-sync/order-shipped",
        { order_ids: orderIds, shipped },
        { headers: authHeader() },
      );
      if (!data?.success || data?.bundlesUpdated === 0) {
        setDetailGroups(before);
        throw new Error(data?.message || "Nothing was updated");
      }
      dispatch(setNotification({ message: data.message, type: "success" }));
      // Same reason as the per-order tick: the rail's shipped counts are a
      // separate query and go stale otherwise.
      getParties();
    } catch (err) {
      setDetailGroups(before);
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
    setShippingGroupKey(null);
  };

  const openDetails = (invoice) => {
    setDetailInvoice(invoice);
    // A stale selection from the previously-opened invoice would filter this
    // one to nothing.
    setSelectedCustomer(null);
    setParties(null);
    setCustomerSearch("");
    setAppliedCustomerSearch("");
    // "All locations". The page-level location tabs were removed, so there is
    // no page filter left to inherit -- the invoice list is unfiltered by
    // location and the modal opens showing everything on the invoice. The
    // dropdown inside the modal still narrows it from here.
    setDetailLocation("");
  };

  const closeDetails = () => {
    setDetailInvoice(null);
    setSelectedCustomer(null);
    setParties(null);
    setCustomerSearch("");
    setAppliedCustomerSearch("");
    setDetailLocation("");
  };

  const applyCustomerSearch = (e) => {
    if (e) e.preventDefault();
    setAppliedCustomerSearch(customerSearch);
    // A customer hidden by the new term must not stay selected and keep
    // filtering the bundle table from behind the search.
    setSelectedCustomer(null);
  };

  const clearCustomerSearch = () => {
    setCustomerSearch("");
    setAppliedCustomerSearch("");
  };

  const pickCustomer = (customer) => {
    // Clicking the selected customer again clears it -- the rail has no
    // separate "all" row, and staff reach for the same row to undo.
    setSelectedCustomer((prev) => (prev?.key === customer.key ? null : customer));
  };

  const openProductsForWo = (woId) => {
    if (woId) setProductsWoId(woId);
  };

  /**
   * The rail list: customers, narrowed by the search box.
   *
   * Matches the NAME or any id the user can see -- the ERP customer id and
   * the source "Cust ID" from the bundle table, since a linked customer shows
   * one in the rail and the other on its rows.
   */
  // A group counts as shipped only when EVERY order on it is -- a
  // part-shipped address still has work outstanding and has to stay visible.
  const isGroupShipped = (g) =>
    g.orderCount > 0 && g.orders.every((o) => o.shipped);
  const shippedGroupCount = detailGroups.filter(isGroupShipped).length;
  const visibleGroups = showShipped
    ? detailGroups
    : detailGroups.filter((g) => !isGroupShipped(g));
  // Only real addresses can be validated -- "no work order linked" and "work
  // order has no shipping address" have nothing to send to UPS. Taken from the
  // VISIBLE groups so the count matches what is on screen: validating a group
  // hidden by the Shipped filter would be a surprise.
  const validatableGroups = visibleGroups.filter((g) => g.kind === "address");

  // Groups that can take part in a Combine. Wider than validatableGroups: the
  // "no address" group belongs here too, as a TARGET -- a linked work order
  // with an empty shipping address is exactly the thing that needs to receive
  // another group's address. It cannot be the SOURCE (nothing to offer), which
  // the modal enforces. "No work order linked" stays out entirely: it has no
  // woId to write an address to.
  //
  // A combine still needs at least one real address to hand out, so the
  // buttons below also check validatableGroups.length -- two no-address groups
  // and nothing else cannot combine into anything.
  const combinableGroups = visibleGroups.filter(
    (g) => g.kind === "address" || g.kind === "no_address",
  );

  // How many work orders a Combine could touch. The button needs two: one
  // order alone has nothing to combine with.
  //
  // Counted across the visible combinable groups, NOT per group: the modal
  // ticks per work order, so a single group holding several orders is a
  // valid Combine (push a corrected address onto some of them). That case
  // used to need its own "Combine WOs" button pair, scoped to one customer,
  // which sat beside this pair doing the same thing whenever every group held
  // one order -- the two pairs were collapsed on 2026-09-09.
  const combinableWoCount = new Set(
    combinableGroups.flatMap((g) =>
      (g.orders || [])
        .map((o) => Number(o.woId))
        .filter((n) => Number.isInteger(n) && n > 0),
    ),
  ).size;

  const shippedCustomerCount = (parties?.customers || []).filter(
    (c) => c.allShipped,
  ).length;

  const railCustomers = (() => {
    // A customer whose every bundle has shipped is done for this invoice, so
    // they leave the rail the same way their addresses leave the queue --
    // otherwise the rail keeps listing someone with nothing left to do.
    const all = (parties?.customers || []).filter(
      (c) => showShipped || !c.allShipped,
    );
    const term = appliedCustomerSearch.trim().toLowerCase();
    if (!term) return all;
    return all.filter((c) => {
      if ((c.name || "").toLowerCase().includes(term)) return true;
      const ids = [
        c.customerId,
        c.sourceCompanyId,
        ...(c.sourceCompanyIds || []),
      ].filter((n) => n !== null && n !== undefined);
      return ids.some((n) => String(n) === term);
    });
  })();

  const getDetailRows = useCallback(async () => {
    if (!detailInvoice?.invoice_number) return;
    setDetailLoading(true);
    try {
      // Grouped shipping address -> order, from the server. Not the paginated
      // /list: grouping a single 25-row page would split one address across
      // pages and show it twice with two partial counts.
      const { data } = await API.post(
        `/bundle-sync/bundle-groups`,
        {
          invoiceNumber: detailInvoice.invoice_number,
          // Inherit the page's filters so the modal shows the same subset the
          // count on the row was calculated from -- otherwise "220 bundles"
          // could open a modal listing a different number.
          query: appliedSearch || undefined,
          filterBy: filterBy || undefined,
          mapped: mappedFilter || undefined,
          // The modal's own dropdown, and nothing else -- the page has no
          // location filter to fall back to.
          locationId: detailLocation || undefined,
          includeDeleted,
          // Customer selection from the rail, applied SERVER-side: this table
          // is paginated, so filtering in the browser would only hide rows on
          // the loaded page.
          woIds:
            selectedCustomer?.linked && selectedCustomer.woIds?.length
              ? selectedCustomer.woIds
              : undefined,
          sourceCompanyId:
            selectedCustomer && !selectedCustomer.linked
              ? selectedCustomer.sourceCompanyId
              : undefined,
          unlinkedOnly:
            selectedCustomer && !selectedCustomer.linked ? true : undefined,
        },
        { headers: authHeader() },
      );
      const groups = data?.data || [];
      setDetailGroups(groups);
      setDetailTotalRows(data?.totalBundles || 0);
      // Open the first group by default: with one address the accordion would
      // otherwise start fully collapsed and look empty.
      setOpenGroups(groups.length ? { [groups[0].key]: true } : {});
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
    setDetailLoading(false);
  }, [
    dispatch,
    detailInvoice,

    appliedSearch,
    filterBy,
    mappedFilter,
    detailLocation,
    includeDeleted,
    selectedCustomer,
  ]);

  useEffect(() => {
    getDetailRows();
  }, [getDetailRows]);

  /**
   * The rail's contents: customers and work orders on this invoice, in one
   * call. Both are groupings of the same bundles, so fetching them separately
   * would only make the rail appear in two stages.
   */
  const getParties = useCallback(async () => {
    if (!detailInvoice?.invoice_number) return;
    setPartiesLoading(true);
    try {
      const { data } = await API.post(
        "/bundle-sync/invoice-parties",
        {
          invoiceNumber: detailInvoice.invoice_number,
          includeDeleted,
          locationId: detailLocation || undefined,
        },
        { headers: authHeader() },
      );
      setParties(data?.success ? data : null);
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
      setParties(null);
    }
    setPartiesLoading(false);
  }, [dispatch, detailInvoice, includeDeleted, detailLocation]);

  useEffect(() => {
    getParties();
  }, [getParties]);

  /**
   * Reload both halves of the Details modal at once.
   *
   * The rail (getParties) and the bundle pane (getDetailRows) are two groupings
   * of the same bundles; refreshing one alone would leave them describing
   * different states of the invoice. Returns the combined promise so the
   * RefreshButton spins until both settle.
   */
  const refreshDetail = useCallback(
    () => Promise.all([getParties(), getDetailRows()]),
    [getParties, getDetailRows],
  );

  /**
   * Drop a selection that has just finished.
   *
   * A fully-shipped customer leaves the rail, and a selection pointing at a
   * row that is no longer listed is a dead end: nothing is highlighted, but
   * the pane is still filtered to them. Clearing shows what is actually left
   * to do. Kept while "Show shipped" is on, because then they ARE still
   * listed and the user is deliberately looking at finished work.
   */
  useEffect(() => {
    if (!selectedCustomer || showShipped || !parties) return;
    const current = parties.customers.find((c) => c.key === selectedCustomer.key);
    if (current?.allShipped) setSelectedCustomer(null);
  }, [parties, selectedCustomer, showShipped]);

  const invoice_table_body = (item) => (
    <tr key={item.invoice_number}>
      <td className="text-nowrap fw-semibold">{item.invoice_number || "-"}</td>
      <td className="text-nowrap">{fmtDate(item.invoice_date)}</td>
      <td className="text-nowrap">
        {item.awb_number || "-"}
        {/* Copied to Shipments (Copy to Shipments in the Details modal). */}
        {item.shipmentId && (
          <span
            className="badge bg-primary bg-opacity-10 text-primary fw-normal ms-1"
            title="This tracking number is in Shipments"
          >
            <i className="mdi mdi-truck-check-outline me-1" aria-hidden="true" />
            In Shipments
          </span>
        )}
      </td>
      <td className="text-center text-nowrap">
        <span className="badge bg-primary">{item.bundleCount}</span>
      </td>
      <td className="text-center text-nowrap">{item.orderCount}</td>
      <td className="text-center text-nowrap">{item.companyCount}</td>
      <td className="text-center text-nowrap">
        {/* Orders, not bundles: the W-ID is an order-level fact, so "4 of 86"
            is the number staff can act on. */}
        {item.mappedOrderCount === item.orderCount ? (
          <span className="badge bg-success">
            {item.mappedOrderCount} / {item.orderCount}
          </span>
        ) : item.mappedOrderCount === 0 ? (
          <span className="badge bg-warning text-dark">
            0 / {item.orderCount}
          </span>
        ) : (
          <span className="badge bg-info">
            {item.mappedOrderCount} / {item.orderCount}
          </span>
        )}
      </td>
      <td className="text-center text-nowrap">
        {/* `lastScan` is absent until an invoice has been scanned -- and
            absent from EVERY row until the API learns to send it -- so the
            same dash the address cards use covers both. A 0/278 here would
            read as a scan that found nothing. */}
        <div className="d-inline-flex align-items-center">
          <ScanStat lastScan={item.lastScan} showLabel={false} />
          {/* Beside the number rather than behind a menu, for the same reason
              as the address card: the packer re-packs the shipment and
              re-scans, and only when there is a result to clear. */}
          {item.lastScan && (
            <button
              type="button"
              className="btn btn-sm btn-link text-muted p-0 ms-1"
              title="Clear this scan result so the invoice can be scanned again"
              onClick={() => {
                if (
                  window.confirm(
                    `Clear the scan result for invoice ${item.invoice_number}? The column goes back to 'not scanned'. Bundles are not changed.`,
                  )
                ) {
                  resetInvoiceScan(item);
                }
              }}
            >
              <i
                className="mdi mdi-refresh fs-5"
                aria-hidden="true"
                style={{ lineHeight: 1 }}
              />
              <span className="visually-hidden">Reset scan</span>
            </button>
          )}
        </div>
      </td>
      <td className="text-end text-nowrap">
        <button
          className="btn btn-sm btn-info"
          onClick={() => openDetails(item)}
          title="Show the bundles on this invoice"
        >
          Details
        </button>
      </td>
    </tr>
  );

  return (
    // marginLeft clears the fixed sidebar. Every other list page hardcodes it
    // the same way (Ups Tracking, Approvals, Customers all use 260); there is
    // no shared layout wrapper to inherit it from.
    <div className="" style={{ marginLeft: 260, height: "100vh" }}>
      {loading && <Spinner />}

      <div className="d-flex px-2 justify-content-between mb-3 align-items-center mx-2 pt-2">
        <div className="d-flex align-items-center gap-2">
          <h2 className="mb-0">Nova Shipping</h2>

          {/* Invoice + AWB picker. A plain <select> cannot work here: the list
              is server-paged and server-searched, because the mirror holds far
              more invoices than are worth sending to the browser. */}
          <div className="position-relative">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              onClick={openInvoiceDropdown}
              style={{ minWidth: 260 }}
            >
              <span className="text-truncate flex-grow-1 text-start">
                {selectedInvoice ? (
                  <>
                    <strong>{selectedInvoice.invoice_number}</strong>
                    {selectedInvoice.awb_number
                      ? ` — ${selectedInvoice.awb_number}`
                      : ""}
                  </>
                ) : (
                  "All invoices"
                )}
              </span>
              <i className="mdi mdi-chevron-down"></i>
            </button>

            {selectedInvoice && (
              <button
                type="button"
                className="btn btn-sm btn-link text-danger position-absolute"
                style={{ right: 28, top: 6 }}
                title="Clear invoice filter"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedInvoice(null);
                  setPageNumber(1);
                }}
              >
                <i className="mdi mdi-close"></i>
              </button>
            )}

            {invoiceOpen && (
              <div
                className="position-absolute bg-white shadow rounded mt-1 p-2"
                style={{
                  zIndex: 1050,
                  width: 340,
                  maxHeight: 380,
                  overflowY: "auto",
                }}
              >
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    loadInvoiceOptions({ search: invoiceSearch });
                  }}
                  className="d-flex gap-1 mb-2"
                >
                  <input
                    className="form-control form-control-sm"
                    placeholder="Search invoice or AWB..."
                    value={invoiceSearch}
                    onChange={(e) => setInvoiceSearch(e.target.value)}
                  />
                  <button className="btn btn-sm btn-info" type="submit">
                    Go
                  </button>
                </form>

                <button
                  type="button"
                  className="btn btn-link btn-sm w-100 text-start px-1"
                  onClick={() => pickInvoice(null)}
                >
                  All invoices
                </button>

                {invoiceOptions.map((o) => (
                  <button
                    key={o.invoice_number}
                    type="button"
                    className="btn btn-link btn-sm w-100 text-start text-decoration-none px-1 border-bottom rounded-0"
                    onClick={() => pickInvoice(o)}
                  >
                    <span className="text-dark fw-bold">
                      {o.invoice_number}
                    </span>
                    <span className="text-muted">
                      {" "}
                      {fmtDate(o.invoice_date)}
                    </span>
                    <div className="small text-muted">
                      {o.awb_number || "no AWB"}
                      {o.awb_count > 1 ? ` +${o.awb_count - 1} more` : ""}
                      {` · ${o.bundle_count} bundle${o.bundle_count === 1 ? "" : "s"}`}
                    </div>
                  </button>
                ))}

                {invoiceLoading && (
                  <div className="text-muted small p-1">Loading...</div>
                )}
                {!invoiceLoading && invoiceOptions.length === 0 && (
                  <div className="text-muted small p-1">No invoices match.</div>
                )}
                {invoiceHasMore && !invoiceLoading && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary w-100 mt-1"
                    onClick={() => loadInvoiceOptions({ append: true })}
                  >
                    Load more
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="d-flex flex-grow-1 justify-content-end align-items-center gap-2">
          {/* "Show <n>" dropdown rather than a select, matching the Work
                Orders and Customers lists. */}
          <div className="d-flex align-items-center gap-1">
            <div>Show</div>
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul style={{ width: "fit-content" }} className="dropdown-menu">
              {[10, 15, 25, 50, 100].map((n) => (
                <li key={n} onClick={() => changeLimit(n)}>
                  <button className="dropdown-item" href="#">
                    {n}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <select
            className="form-select form-select-sm w-auto"
            value={mappedFilter}
            onChange={(e) => {
              setPageNumber(1);
              setMappedFilter(e.target.value);
            }}
          >
            <option value="">All rows</option>
            <option value="mapped">Mapped only</option>
            <option value="unmapped">Not mapped</option>
          </select>

          <div className="form-check ms-1">
            <input
              className="form-check-input"
              type="checkbox"
              id="bs-deleted"
              checked={includeDeleted}
              onChange={(e) => {
                setPageNumber(1);
                setIncludeDeleted(e.target.checked);
              }}
            />
            <label className="form-check-label small" htmlFor="bs-deleted">
              Show removed
            </label>
          </div>

          <form onSubmit={applySearch} className="d-flex gap-1">
            <select
              className="form-select form-select-sm w-auto"
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
            >
              {SEARCH_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <input
              className="form-control form-control-sm"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-sm btn-info" type="submit">
              Search
            </button>
            <RefreshButton
              className="btn btn-sm btn-outline-primary"
              loading={loading}
              onClick={getRows}
            />
            {(appliedSearch || mappedFilter || filterBy) && (
              <button
                className="btn btn-sm btn-danger"
                type="button"
                onClick={clearSearch}
              >
                Clear
              </button>
            )}
          </form>
          <CopyAllToShipments onDone={getRows} />
        </div>
      </div>

      {/* Shipped-status tabs, ABOVE the location tabs: which invoices are
          still outstanding is the first cut a packer makes, and the location
          narrows within it.

          Pending = not in Shipments yet (user, 2026-10-29), whatever the
          bundles' shipped marks say; once copied it carries the "In
          Shipments" label under All. No Completed tab (user, 2026-10-26). */}
      <div className="mx-2 rounded-1 overflow-hidden mb-2 shadow-sm d-inline-flex">
        <div className="btn-group flex-wrap">
          {[
            ["pending", "Pending"],
            ["", "All"],
          ].map(([value, label]) => (
            <button
              key={label}
              className={
                shippedStatus === value
                  ? "btn btn-sm py-1 btn-primary"
                  : "btn btn-sm py-1 bg-white"
              }
              onClick={() => changeShippedStatus(value)}
              title={value === "pending" ? "Invoices not in Shipments yet" : undefined}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {mappedFilter && (
        <div className="alert alert-info py-1 px-2 mb-2 mx-2 small">
          The mapped/not-mapped filter applies to the rows on this page, so a
          page can show fewer than {pageLimit} rows. Narrow with a search to
          work through them.
        </div>
      )}

      {/* Dimmed and click-through while a fetch is in flight. The spinner alone
          is easy to miss against a full table, and worse, the rows underneath
          are STALE -- clicking "Link WO" on one would open the modal for a row
          that is about to be replaced by a different page of results. */}
      <div
        className="px-2 flex-grow-1 overflow-y-auto d-flex"
        style={{
          // 84vh before the location tabs existed. The tab strip added a row
          // above the table, which pushed the pager off the bottom of the
          // viewport -- this is the height that fits header + tabs + pager.
          height: "78vh",
          overflowY: "auto",
          borderRadius: 15,
          opacity: loading ? 0.4 : 1,
          pointerEvents: loading ? "none" : "auto",
          transition: "opacity 120ms ease",
        }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={invoice_table_head}
            table_body={invoice_table_body}
            table_data={rows}
            tableSize="sm"
            disableSorting={true}
            actionsOnEnd={true}
            hoverable={true}
          />
        </div>
      </div>

      {!loading && rows.length === 0 && (
        <div className="text-center text-muted py-4">
          No invoices match this search.
        </div>
      )}

      {/* Count sits opposite the pager, so the row reads
          "how many there are" ... "where you are in them". */}
      <div className="d-flex justify-content-between align-items-center mt-2 mx-2">
        <span className="text-muted text-nowrap">
          {totalRows.toLocaleString()} invoice{totalRows === 1 ? "" : "s"}
        </span>
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      {/* Per-invoice bundle detail. Same columns and the same Link WO button
          the page used to show inline, just scoped to one invoice. */}
      {/* Bootstrap's xl modal caps at 1140px, which clipped the Actions
          column. Widened -- but bounded by the SIDEBAR, not the viewport: the
          modal renders in a portal on <body>, outside this page's
          marginLeft:260 wrapper, and the fixed sidebar is z-index 99999. The
          modal used to be inset by the sidebar's width to stay clear of it;
          it now spans the full window instead and the whole stack is raised
          ABOVE the nav. See the z-index block below -- widening this was a
          stacking change, not a width change. */}
      <style>
        {`
          .nova-shipping-detail-modal {
            /* Width comes from the margins, NOT from 100vw arithmetic: vw
               includes the scrollbar, and Bootstrap also pads the body by the
               scrollbar width while a modal is open, so a 100vw-derived width
               overshot and sat flush against the right edge. Letting the
               dialog fill its container between explicit margins is immune to
               both. */
            max-width: none;
            width: auto;
            margin-left: 1rem;
            margin-right: 1rem;
            height: calc(100vh - 2rem);
          }
          .nova-shipping-detail-modal .modal-content {
            height: 100%;
          }
          /* Neutral body so the white cards read as cards. White-on-white had
             only hairline borders separating the rail from the pane. */
          .nova-shipping-detail-modal .modal-body {
            background: #f0f5f9;
          }
          /*   Stacking
           *
           * The sidebar is fixed at z-index 99999 (NavBar.js), so everything
           * here has to clear that or the modal is painted over on its left
           * edge. Bootstrap's defaults (modal 1055, backdrop 1050) are far
           * below it, which is why this whole ladder is explicit.
           *
           * The order matters as much as the height: each modal opens FROM
           * the one below it, and a modal whose backdrop lands under its
           * opener makes the two look blended together -- exactly what the
           * Link WO modal did on 2026-08-27. Backdrop always sits directly
           * under its own modal.
           */
          .ns-details-backdrop { z-index: 100000; }
          .ns-details-modal    { z-index: 100001; }
          .ns-edit-backdrop    { z-index: 100020; }
          .ns-edit-modal       { z-index: 100021; }

          /* The rail rows are buttons so they are keyboard-reachable; these
             strip the button chrome back to a list row. */
          .ns-rail-item {
            display: block;
            width: 100%;
            text-align: left;
            border: 0;
            border-bottom: 1px solid #eee;
            background: transparent;
            padding: 6px 8px;
          }
          .ns-rail-item:hover { background: #f6f9ff; }
          .ns-rail-item.active { background: #e7f1ff; }
          /* Fully shipped: only visible while Show shipped is on, and greyed
             so it reads as done rather than as outstanding work. Muted rather
             than hidden-looking -- it is still selectable, and the counts on
             it are still true. */
          .ns-rail-item-shipped {
            background: #f4f5f7;
            opacity: 0.62;
          }
          .ns-rail-item-shipped:hover { background: #eceef1; }
          .ns-rail-item-shipped.active { background: #e2e6ea; opacity: 1; }
          @media (max-width: 767.98px) {
            .nova-shipping-detail-modal {
              margin-left: 0.5rem;
              margin-right: 0.5rem;
              height: calc(100vh - 1rem);
            }
          }
        `}
      </style>
      <Modal
        show={!!detailInvoice}
        onHide={closeDetails}
        dialogClassName="nova-shipping-detail-modal"
        className="ns-details-modal"
        backdropClassName="ns-details-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            Invoice {detailInvoice?.invoice_number}
            <span className="text-muted fs-6 ms-2">
              {detailInvoice?.awb_number
                ? `AWB ${detailInvoice.awb_number} · `
                : ""}
              {fmtDate(detailInvoice?.invoice_date)} ·{" "}
              {detailTotalRows.toLocaleString()} bundle
              {detailTotalRows === 1 ? "" : "s"}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex gap-2" style={{ overflow: "hidden" }}>
          {/*   Left rail: the customers on this invoice
               The Work orders card that used to sit under this one is gone;
               a work order now opens from the W-ID on its bundle row.
           **************** */}
          <div
            className="d-flex flex-column"
            style={{ flex: "0 0 260px", minWidth: 260, minHeight: 0 }}
          >
            <div
              className="border rounded bg-white d-flex flex-column"
              style={{ flex: "1 1 auto", minHeight: 0 }}
            >
              {/* Title row, then Show shipped underneath it -- both inside the
                  tinted header, so what it hides sits with the rail it hides
                  it from rather than over the bundle pane.
                  minHeight pins the title row to the same height as the
                  work-order pane's header, which carries a button and would
                  otherwise sit taller than this text-only one. */}
              <div className="border-bottom bg-primary bg-opacity-10">
                <div
                  className="px-2 py-1 small fw-bold text-uppercase d-flex justify-content-between align-items-center"
                  style={{ minHeight: 42 }}
                >
                  <span>
                    Customers
                    {parties
                      ? ` (${railCustomers.length}${
                          railCustomers.length !== parties.customers.length
                            ? ` / ${parties.customers.length}`
                            : ""
                        })`
                      : ""}
                  </span>
                  <div className="d-flex align-items-center gap-1">
                    {selectedCustomer && (
                      <button
                        className="btn btn-sm btn-outline-primary py-0 px-2 lh-base"
                        style={{ fontSize: 11 }}
                        title="Show every customer's bundles again"
                        onClick={() => {
                          setSelectedCustomer(null);
                        }}
                      >
                        clear
                      </button>
                    )}
                    {/* Re-pull the rail AND the bundle pane together -- both are
                        groupings of the same bundles, so refreshing one without
                        the other would leave the two showing different states
                        of the invoice (e.g. after a combine on another PC, or
                        an address edit). */}
                    <RefreshButton
                      className="btn btn-sm btn-outline-primary py-0 px-2 lh-base"
                      loading={partiesLoading || detailLoading}
                      title="Reload this invoice's customers and bundles"
                      onClick={refreshDetail}
                    />
                  </div>
                </div>

                {/* Shown whenever ANYTHING is hidden -- a customer can be
                    fully shipped and gone from the rail while the pane's
                    current selection has no shipped groups of its own, and
                    without this there would be no way back to them. */}
                {(shippedGroupCount > 0 || shippedCustomerCount > 0) && (
                  <div className="px-2 pb-2">
                    <button
                      className={`btn btn-sm w-100 ${showShipped ? "btn-secondary" : "btn-outline-secondary"}`}
                      onClick={() => setShowShipped((v) => !v)}
                      title={
                        shippedCustomerCount > 0
                          ? `${shippedCustomerCount} customer${shippedCustomerCount === 1 ? "" : "s"} and their addresses have left the queue; this brings them back into view`
                          : "Shipped addresses leave the queue; this brings them back into view"
                      }
                    >
                      <i className="mdi mdi-truck-check me-1" aria-hidden="true" />
                      {showShipped ? "Hide" : "Show"} shipped (
                      {Math.max(shippedGroupCount, shippedCustomerCount)})
                    </button>
                  </div>
                )}
              </div>

              {/* Search + location. Both narrow the rail; the location one
                  also narrows the bundle table, because the counts beside
                  each customer are read as describing it. */}
              <div className="p-2 border-bottom d-flex flex-column gap-1">
                <form
                  className="d-flex gap-1"
                  onSubmit={applyCustomerSearch}
                >
                  <input
                    className="form-control form-control-sm"
                    placeholder="Search name or Cust ID"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                  />
                  <button
                    className="btn btn-sm btn-info text-nowrap"
                    type="submit"
                    title="Search customers by name or Cust ID"
                  >
                    <i className="mdi mdi-magnify"></i>
                  </button>
                  {appliedCustomerSearch && (
                    <button
                      className="btn btn-sm btn-outline-danger text-nowrap"
                      type="button"
                      title="Clear the customer search"
                      onClick={clearCustomerSearch}
                    >
                      <i className="mdi mdi-close"></i>
                    </button>
                  )}
                </form>
                <select
                  className="form-select form-select-sm"
                  value={detailLocation}
                  onChange={(e) => {
                    setDetailLocation(e.target.value);
                    // The old selection may not exist under the new filter.
                    setSelectedCustomer(null);
                                  }}
                  title="ERP location, which comes from the linked work order"
                >
                  <option value="">All locations</option>
                  {locationsList.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.locationName}
                    </option>
                  ))}
                  <option value="unmapped">Not mapped</option>
                </select>
              </div>

              <div style={{ overflowY: "auto", minHeight: 0 }}>
                {partiesLoading && (
                  <div className="text-muted small p-2">Loading…</div>
                )}
                {!partiesLoading && railCustomers.length === 0 && (
                  <div className="text-muted small p-2">
                    {appliedCustomerSearch
                      ? "No customer matches that name or Cust ID."
                      : "No customers for this filter."}
                  </div>
                )}
                {railCustomers.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    // Fully-shipped customers only appear while Show shipped
                    // is on, and then they are greyed so they read as done
                    // rather than as more work sitting in the queue.
                    className={
                      "ns-rail-item" +
                      (selectedCustomer?.key === c.key ? " active" : "") +
                      (c.allShipped ? " ns-rail-item-shipped" : "")
                    }
                    onClick={() => pickCustomer(c)}
                    title={
                      c.linked
                        ? "Filter the bundles to this customer"
                        : "Bundles from this source company with no W-ID yet"
                    }
                  >
                    <div className="d-flex justify-content-between gap-1">
                      <span
                        className={
                          "small text-truncate " +
                          (c.linked ? "fw-semibold" : "text-muted")
                        }
                      >
                        {c.name}
                      </span>
                      {/* Addresses, not bundles: this is the number of
                          accordions that open when the customer is picked,
                          which is what says how much work they are. A bundle
                          count answers a question nobody asks from the rail. */}
                      {/* Green for the normal case of ONE address, red when a
                          customer's bundles are going to several -- that is
                          the one that needs a human to look at it, so it
                          should be the one that catches the eye. */}
                      <span
                        className={`badge ${
                          (c.addressCount ?? 0) > 1 ? "bg-danger" : "bg-success"
                        }`}
                        title={`${c.addressCount ?? 0} shipping address${(c.addressCount ?? 0) === 1 ? "" : "es"}`}
                      >
                        {c.addressCount ?? 0}
                      </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {/* Unlinked customers have no work orders by definition,
                          so say why rather than printing "0 WOs". */}
                      {c.linked
                        ? `${c.woCount} WO${c.woCount === 1 ? "" : "s"} · ${c.bundleCount} bundle${c.bundleCount === 1 ? "" : "s"}`
                        : `not linked · ${c.bundleCount} bundle${c.bundleCount === 1 ? "" : "s"}`}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/*   Right pane: bundles, or one work order
           **************** */}
          {/* Background depends on what this pane is showing.
              Bundles: bg-primary2 (#f0f5f9), because the accordion groups are
              white CARDS and a white card on a white pane is invisible as a
              card -- the fix the modal body needed on 08-27.
              A work order: plain white, because that view is one continuous
              surface with no cards to separate. */}
          <div
            className="border rounded d-flex flex-column flex-grow-1 p-2 bg-primary2"
            style={{ minWidth: 0, minHeight: 0 }}
          >
            <>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small text-muted">
                    {selectedCustomer ? (
                      <>
                        Bundles for{" "}
                        <strong>{selectedCustomer.name}</strong>
                      </>
                    ) : (
                      "All bundles on this invoice"
                    )}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    {/* The Show shipped toggle moved under the Customers
                        header -- what it hides is mostly rail entries, so it
                        belongs with the rail rather than over the bundle
                        pane. */}
                    {/* Validate every real address on the invoice in one
                        pass, rather than pressing the per-group button down a
                        long list. Only the address groups: the two "nothing to
                        ship to" buckets have no address to check.

                        Hidden when there is nothing to validate, and when
                        there is only ONE group -- that group's own button is
                        right there and does exactly the same thing. */}
                    {/* Two buttons, one modal. Merging and validating are
                        separate questions: an address can be known good and
                        still be split across groups, and then a UPS call is a
                        round trip that answers nothing. Combine writes the
                        chosen address as it stands; Validate & Combine checks
                        it first. */}
                    {/* Needs two work orders and at least one real address to
                        hand out -- two no-address groups cannot combine into
                        anything. The count is work orders, because that is
                        what the modal ticks. */}
                    {combinableWoCount > 1 &&
                      validatableGroups.length >= 1 && (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            setCombineMode("combine");
                            setCombiningGroups(combinableGroups);
                          }}
                          title="Put the ticked work orders onto one chosen address, without calling UPS. Tick whole addresses or single orders; one with no address can be included to give it one."
                        >
                          <i
                            className="mdi mdi-call-merge me-1"
                            aria-hidden="true"
                          />
                          Combine ({combinableWoCount})
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            setCombineMode("validate");
                            setCombiningGroups(combinableGroups);
                          }}
                          title="Check the chosen address with UPS, then put the ticked work orders onto it"
                        >
                          <i
                            className="mdi mdi-map-marker-check me-1"
                            aria-hidden="true"
                          />
                          Validate &amp; Combine
                        </button>
                      </>
                    )}

                    {/* The whole invoice, whatever the rail has selected:
                        one tracking number, one Shipments row. */}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setCopyInvoice(detailInvoice?.invoice_number || null)}
                      title="Copy this invoice's tracking number to Shipments (PK-UPS / PK-DHL), linked to a work order and its customer"
                    >
                      <i className="mdi mdi-truck-plus-outline me-1" aria-hidden="true" />
                      Copy to Shipments
                    </button>

                    <span className="small text-muted">
                      {detailTotalRows.toLocaleString()} bundle
                      {detailTotalRows === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>

                {detailLoading && (
                  <div className="text-center text-muted py-3">
                    Loading bundles…
                  </div>
                )}
                {!detailLoading && detailGroups.length === 0 && (
                  <div className="text-center text-muted py-3">
                    No bundles match this selection.
                  </div>
                )}
                {!detailLoading && detailGroups.length > 0 && (
                  <div
                    style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}
                  >
                    {visibleGroups.map((g) => (
                      <ShippingAddressGroup
                        key={g.key}
                        group={g}
                        open={!!openGroups[g.key]}
                        onToggle={() =>
                          setOpenGroups((prev) => ({
                            ...prev,
                            [g.key]: !prev[g.key],
                          }))
                        }
                        onOpenWorkOrder={openProductsForWo}
                        onEditMapping={openEdit}
                        onToggleShipped={toggleOrderShipped}
                        onShipGroup={shipGroup}
                        onValidateGroup={(g, opts) => {
                          setValidateForce(!!opts?.force);
                          setValidatingGroups([g]);
                        }}
                        onOpenAddressHistory={setHistoryWoId}
                        onResetScan={resetGroupScan}
                        shippingOrderId={shippingOrderId}
                        shippingGroup={shippingGroupKey === g.key}
                      />
                    ))}
                    {visibleGroups.length === 0 && (
                      <div className="text-center text-muted py-4">
                        {selectedCustomer
                          ? `Everything for ${selectedCustomer.name} is marked shipped.`
                          : "Everything on this invoice is marked shipped."}
                      </div>
                    )}
                  </div>
                )}
            </>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <span className="text-muted small">
            {parties?.totals
              ? `${parties.totals.customerCount} customer${parties.totals.customerCount === 1 ? "" : "s"} · ${parties.totals.workOrderCount} work order${parties.totals.workOrderCount === 1 ? "" : "s"} · ${parties.totals.bundleCount.toLocaleString()} bundle${parties.totals.bundleCount === 1 ? "" : "s"}${detailLocation ? " (filtered)" : ""}`
              : ""}
          </span>
          <div className="d-flex align-items-center gap-2">
            {/* No pager: the grouped view returns the whole selection at once,
                because a group split across pages would show twice with two
                partial counts. */}
            <button className="btn btn-secondary" onClick={closeDetails}>
              Close
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {productsWoId && (
        <WorkOrderProductsModal
          woId={productsWoId}
          onHide={() => setProductsWoId(null)}
        />
      )}

      {historyWoId && (
        <AddressHistoryModal
          woId={historyWoId}
          onHide={() => setHistoryWoId(null)}
        />
      )}

      {copyInvoice && (
        <CopyToShipmentsModal
          invoiceNumber={copyInvoice}
          onHide={() => setCopyInvoice(null)}
          // The invoice list's "In Shipments" badge.
          onCopied={() => getRows()}
        />
      )}

      {combiningGroups && (
        <CombineAddressesModal
          groups={combiningGroups}
          mode={combineMode}
          onHide={() => setCombiningGroups(null)}
          onApplied={(appliedCount, skipped) => {
            setCombiningGroups(null);
            dispatch(
              setNotification({
                message: appliedCount
                  ? `${appliedCount} work order${appliedCount === 1 ? "" : "s"} moved onto the chosen address — regrouping`
                  : "Nothing was changed",
                type: appliedCount ? "success" : "info",
              }),
            );
            if (skipped?.length) {
              dispatch(
                setNotification({
                  message: `${skipped.length} work order${skipped.length === 1 ? " was" : "s were"} skipped`,
                  type: "warning",
                }),
              );
            }
            // The groups are keyed on the address, so they only merge once the
            // server rebuilds them.
            getDetailRows();
          }}
        />
      )}

      {validatingGroups && (
        <AddressValidationModal
          groups={validatingGroups}
          force={validateForce}
          onHide={() => setValidatingGroups(null)}
          onApplied={(appliedCount, skipped) => {
            setValidatingGroups(null);
            dispatch(
              setNotification({
                message: appliedCount
                  ? `${appliedCount} address${appliedCount === 1 ? "" : "es"} corrected — regrouping`
                  : "No addresses were changed",
                type: appliedCount ? "success" : "info",
              }),
            );
            if (skipped && skipped.length) {
              dispatch(
                setNotification({
                  message: `${skipped.length} order${skipped.length === 1 ? " was" : "s were"} skipped`,
                  type: "warning",
                }),
              );
            }
            // The whole point of applying: the groups are keyed on the
            // address, so they only re-form once the server rebuilds them.
            getDetailRows();
          }}
        />
      )}

      <Modal
        show={!!editRow}
        onHide={() => setEditRow(null)}
        centered
        className="ns-edit-modal"
        backdropClassName="ns-edit-backdrop"
        contentClassName="shadow-lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            Work order for source order {editRow?.order_id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning py-1 px-2 small">
            This applies to{" "}
            <strong>every bundle on order {editRow?.order_id}</strong>, not just
            this row.
          </div>

          <div className="mb-2">
            <label className="form-label mb-1">Work order</label>

            {selectedWo ? (
              <div className="d-flex align-items-center justify-content-between border rounded p-2 bg-light">
                <div>
                  <span className="badge bg-success me-2">
                    W-{selectedWo.woId}
                  </span>
                  <strong>{selectedWo.customerName || "-"}</strong>
                  <span className="text-muted">
                    {" "}
                    — {selectedWo.jobName || "-"}
                  </span>
                  <div className="small text-muted">
                    ERP location: {selectedWo.locationName || "none set"}
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setSelectedWo(null);
                    setEditWoId("");
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                {/* Staff know the customer and the job, not the W number. */}
                <form onSubmit={searchWorkOrders} className="d-flex gap-1">
                  <input
                    className="form-control"
                    placeholder="Search by customer, job name or W number"
                    value={woQuery}
                    onChange={(e) => setWoQuery(e.target.value)}
                  />
                  <button
                    className="btn btn-info text-nowrap"
                    type="submit"
                    disabled={woSearching || !woQuery.trim()}
                  >
                    {woSearching ? "..." : "Search"}
                  </button>
                </form>

                {woResults && (
                  <div
                    className="border rounded mt-1"
                    style={{ maxHeight: 220, overflowY: "auto" }}
                  >
                    {woResults.length === 0 ? (
                      <div className="text-muted small p-2">
                        No work orders match that.
                      </div>
                    ) : (
                      woResults.map((wo) => (
                        <button
                          key={wo.woId}
                          type="button"
                          className="btn btn-link text-decoration-none text-start w-100 border-bottom rounded-0 py-1 px-2"
                          onClick={() => pickWorkOrder(wo)}
                        >
                          <span className="badge bg-secondary me-2">
                            W-{wo.woId}
                          </span>
                          <strong className="text-dark">
                            {wo.customerName || "-"}
                          </strong>
                          <span className="text-muted">
                            {" "}
                            — {wo.jobName || "-"}
                            {wo.subJobName ? ` / ${wo.subJobName}` : ""}
                          </span>
                          <span className="text-muted small">
                            {wo.locationName ? `  ·  ${wo.locationName}` : ""}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
            <div className="form-text">
              The ERP location for these bundles comes from the work order you
              pick.
            </div>
          </div>

          <div className="mb-1">
            <label className="form-label mb-1">Note</label>
            <input
              className="form-control"
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              maxLength={255}
            />
            <div className="form-text">
              For orders with no work order. Leaving both empty clears the
              entry.
            </div>
          </div>

          {editRow?.source_erp_order_number ? (
            <div className="text-muted small mt-2">
              The source server calls this order{" "}
              <strong>{editRow.source_erp_order_number}</strong> — shown for
              reference, it is often blank or inconsistent upstream.
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setEditRow(null)}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={saveMapping}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
